import { getAuth } from 'firebase/auth';
import React from 'react';

import { api } from '@/utils/api';

import { useExtensionContext } from '~/components/extension.context';

/**
 * How long a port `LOGIN` stays good for this page before a reconnect re-sends it.
 *
 * The extension's service worker drops the dashboard port whenever Chrome idles it out, and the
 * provider reconnects (→ `CONNECT_AWK` → `connected`). Re-sending `LOGIN` on every one of those
 * reconnects cost an `auth.token2` call, a Firebase custom-token sign-in, and a `user.me` call
 * from the extension each time — in prod (2026-10-15) that was a burst every ~30s per open tab,
 * nearly all `auth.token2` + `user.me` traffic, from a handful of browsers. The extension persists
 * its auth (and `LOGIN_DIRECT` below refreshes it on page load), so a reconnect for the same
 * Firebase user only needs a re-send once this has elapsed. A different user re-sends at once.
 */
const LOGIN_RESEND_AFTER_MS = 30 * 60 * 1000;

// Module scope (not a ref) so it survives the layout remounts in _app.tsx — each layout calls the
// hook, and switching layouts must not count as a fresh login.
let lastPortLogin: { extensionId: string; userId: string; at: number } | undefined;

export const useLoginExtension = () => {
  const [{ extensionId, extensionConnectedStatus }, { postMessage }] = useExtensionContext();
  const generateTokenMutation = api.auth.token2.useMutation();
  const mutateAsync = generateTokenMutation.mutateAsync;

  // Send auth to extension via chrome.runtime port (existing flow)
  React.useEffect(() => {
    if (!extensionId || extensionConnectedStatus !== 'connected') return;
    const userId = getAuth().currentUser?.uid;
    if (
      userId != null &&
      lastPortLogin != null &&
      lastPortLogin.extensionId === extensionId &&
      lastPortLogin.userId === userId &&
      Date.now() - lastPortLogin.at < LOGIN_RESEND_AFTER_MS
    ) {
      return;
    }
    mutateAsync({
      userAgent: window.navigator.userAgent,
    })
      .then((token) => {
        postMessage({ type: 'LOGIN', data: { token } });
        if (userId != null) lastPortLogin = { extensionId, userId, at: Date.now() };
      })
      .catch((error) => {
        console.error(error);
      });
  }, [mutateAsync, extensionId, extensionConnectedStatus, postMessage]);

  // Also send auth via window.postMessage for content script (side panel support)
  // This runs when the extension is detected on the page
  React.useEffect(() => {
    if (!extensionId) return;

    const sendAuthToContentScript = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        // Get fresh token
        const idToken = await user.getIdToken(true);

        // Get token result for expiration info
        const tokenResult = await user.getIdTokenResult();
        const expirationTime = new Date(tokenResult.expirationTime).getTime();
        const expiresInSeconds = Math.floor((expirationTime - Date.now()) / 1000);

        // Note: Firebase client SDK doesn't expose refreshToken directly
        // We need to get it from the internal auth state
        const refreshToken = (user as unknown as { stsTokenManager?: { refreshToken?: string } })
          .stsTokenManager?.refreshToken;

        if (!refreshToken) {
          console.warn('[useLoginExtension] Could not get refresh token');
          return;
        }

        // Send auth data to content script via window.postMessage
        window.postMessage(
          {
            type: 'LOGIN_DIRECT',
            data: {
              idToken,
              refreshToken,
              userId: user.uid,
              email: user.email ?? '',
              expiresInSeconds,
            },
          },
          '*',
        );
      } catch (error) {
        console.error('[useLoginExtension] Failed to send auth to content script:', error);
      }
    };

    // Small delay to ensure content script is ready
    const timeout = setTimeout(() => {
      void sendAuthToContentScript();
    }, 500);
    return () => clearTimeout(timeout);
  }, [extensionId]);
};
